import { useEffect, useRef, useState } from 'preact/hooks';

import { cx, Icon } from '@amumin/ui-core';

import styles from './Toast.module.css';

export interface ToastOptions {
  message: string;
  kind?: 'info' | 'success' | 'error';
  /** Vergevingsgezind: "Ongedaan maken" na verwijderen/afronden (docs/DESIGN.md §2). */
  actionLabel?: string;
  onAction?: () => void;
  /** Standaard 5 s, met actie 8 s; fouten blijven tot ze gesloten worden. */
  durationMs?: number;
}

interface ToastEntry extends ToastOptions {
  id: number;
}

type Listener = (items: ReadonlyArray<ToastEntry>) => void;

let items: ToastEntry[] = [];
let nextId = 1;
const listeners = new Set<Listener>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit() {
  for (const l of listeners) l(items);
}

/** Toont een melding onderaan; geeft een functie terug die hem weer sluit. */
export function showToast(options: ToastOptions): () => void {
  const id = nextId++;
  // Maximaal drie tegelijk: rustig, geen stapel.
  items = [...items.slice(-2), { ...options, id }];
  emit();
  const duration =
    options.durationMs ?? (options.kind === 'error' ? 0 : options.actionLabel ? 8000 : 5000);
  if (duration > 0)
    timers.set(
      id,
      setTimeout(() => dismissToast(id), duration),
    );
  return () => dismissToast(id);
}

export function dismissToast(id: number): void {
  const t = timers.get(id);
  if (t) clearTimeout(t);
  timers.delete(id);
  items = items.filter((i) => i.id !== id);
  emit();
}

// Modale vensters (Dialog, native showModal): zolang er één open is, ligt de rest van de pagina
// onder de achtergrond en is die inert. Een melding in de schil zou dan gedimd onder de achtergrond
// staan en een tik op "Ongedaan maken" sluit het venster in plaats van iets terug te draaien
// (review c-ux r1). Daarom toont het bovenste open venster de meldingen zelf, in de toplaag.
const layers: number[] = [];
let nextLayer = 1;
const layerListeners = new Set<() => void>();
// Sluitknop-tekst van de schil, zodat een venster dezelfde (vertaalde) naam gebruikt.
let shellDismissLabel = '';

function emitLayers() {
  for (const l of layerListeners) l();
}

/** Een modaal venster gaat open: meldingen verhuizen naar dat venster. Geeft zijn laag-id. */
export function openToastLayer(): number {
  const id = nextLayer++;
  layers.push(id);
  emitLayers();
  return id;
}

/** Het venster is dicht: meldingen gaan terug naar het venster eronder of de schil. */
export function closeToastLayer(id: number): void {
  const i = layers.indexOf(id);
  if (i < 0) return;
  layers.splice(i, 1);
  emitLayers();
}

function topLayer(): number | null {
  return layers.length > 0 ? (layers[layers.length - 1] ?? null) : null;
}

export interface ToastRegionProps {
  /** Toegankelijke naam van de sluitknop (i18n `design.toast.dismiss`). */
  dismissLabel: string;
}

/** Eén keer in de app-schil plaatsen; toont meldingen van showToast(). */
export function ToastRegion({ dismissLabel }: ToastRegionProps) {
  shellDismissLabel = dismissLabel;
  return (
    <ToastList
      layer={null}
      dismissLabel={dismissLabel}
      onSpace={(px) => document.documentElement.style.setProperty('--toast-space', `${px}px`)}
    />
  );
}

/** In een modaal venster (Dialog): toont de meldingen zolang dit venster bovenaan ligt. */
export function DialogToasts({
  layer,
  sheet,
  onSpace,
}: {
  layer: number | null;
  sheet?: boolean;
  /** Hoeveel ruimte de zichtbare meldingen innemen (px, 0 = geen), om die vrij te houden. */
  onSpace?: (px: number) => void;
}) {
  if (layer === null) return null;
  return (
    <ToastList layer={layer} dismissLabel={shellDismissLabel} sheet={sheet} onSpace={onSpace} />
  );
}

// Ruimte onder de meldingen tot de inhoud erachter (px), bij het vrijhouden van ruimte.
const SPACE_GAP = 12;

function ToastList({
  layer,
  dismissLabel,
  sheet = false,
  onSpace,
}: {
  layer: number | null;
  dismissLabel: string;
  sheet?: boolean | undefined;
  onSpace?: ((px: number) => void) | undefined;
}) {
  const [list, setList] = useState<ReadonlyArray<ToastEntry>>(items);
  const regionRef = useRef<HTMLDivElement>(null);
  const spaceRef = useRef(onSpace);
  spaceRef.current = onSpace;
  const [top, setTop] = useState<number | null>(topLayer());
  useEffect(() => {
    const l: Listener = (next) => setList(next);
    const t = () => setTop(topLayer());
    listeners.add(l);
    layerListeners.add(t);
    t();
    return () => {
      listeners.delete(l);
      layerListeners.delete(t);
    };
  }, []);
  const visible = top === layer ? list : [];
  // Review c-ux r3: een melding lag over de knop Bewerken van het blad en over de volgende rit.
  // De schil en het venster houden zolang er een melding staat evenveel ruimte vrij onder de
  // inhoud (padding), zodat alles erboven te zien en aan te tikken blijft.
  useEffect(() => {
    const el = regionRef.current;
    const report = () => {
      const h = el && visible.length > 0 ? el.offsetHeight : 0;
      spaceRef.current?.(h > 0 ? h + SPACE_GAP : 0);
    };
    report();
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [visible.length]);
  useEffect(() => () => spaceRef.current?.(0), []);
  return (
    <div
      ref={regionRef}
      class={cx(styles.region, layer !== null && styles.inDialog, sheet && styles.inSheet)}
      aria-live="polite"
      aria-relevant="additions"
      data-name={layer === null ? 'toasts' : 'dialog-toasts'}
    >
      {visible.map((t) => (
        <div
          key={t.id}
          class={cx(
            styles.toast,
            t.kind === 'success' && styles.success,
            t.kind === 'error' && styles.error,
          )}
          role={t.kind === 'error' ? 'alert' : 'status'}
        >
          {t.kind === 'success' ? (
            <Icon id="circle-check" size={20} class={styles.icon} />
          ) : t.kind === 'error' ? (
            <Icon id="circle-alert" size={20} class={styles.icon} />
          ) : null}
          <span class={styles.message}>{t.message}</span>
          {t.actionLabel ? (
            <button
              type="button"
              class={styles.action}
              onClick={() => {
                if (t.onAction) t.onAction();
                dismissToast(t.id);
              }}
            >
              {t.actionLabel}
            </button>
          ) : null}
          <button
            type="button"
            class={styles.dismiss}
            aria-label={dismissLabel}
            onClick={() => dismissToast(t.id)}
          >
            <Icon id="x" size={18} />
          </button>
        </div>
      ))}
    </div>
  );
}
